import React, { useState, useEffect } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import { CategoriaWithSubcategorias } from '../types/categoria.types';

interface ProductoCategorySelectorProps {
  categoriaId?: number | null;
  subcategoriaId?: number | null;
  onChange: (categoriaId: number | null, subcategoriaId: number | null) => void;
  categorias: CategoriaWithSubcategorias[];
  loading?: boolean;
  error?: string | null;
  required?: boolean;
}

export function ProductoCategorySelector({
  categoriaId,
  subcategoriaId,
  onChange,
  categorias,
  loading = false,
  error,
  required = false,
}: ProductoCategorySelectorProps) {
  const [selectedCategoriaId, setSelectedCategoriaId] = useState<number | null>(
    categoriaId || null
  );
  const [selectedSubcategoriaId, setSelectedSubcategoriaId] = useState<number | null>(
    subcategoriaId || null
  );

  useEffect(() => {
    setSelectedCategoriaId(categoriaId || null);
    setSelectedSubcategoriaId(subcategoriaId || null);
  }, [categoriaId, subcategoriaId]);

  const handleCategoriaChange = (value: number | '') => {
    const newCategoriaId = value === '' ? null : value;
    setSelectedCategoriaId(newCategoriaId);
    setSelectedSubcategoriaId(null);
    onChange(newCategoriaId, null);
  };

  const handleSubcategoriaChange = (value: number | '') => {
    const newSubcategoriaId = value === '' ? null : value;
    setSelectedSubcategoriaId(newSubcategoriaId);
    onChange(selectedCategoriaId, newSubcategoriaId);
  };

  const activeCategorias = categorias.filter((cat) => cat.activo);
  const selectedCategoria = categorias.find((cat) => cat.id === selectedCategoriaId);
  const availableSubcategorias = selectedCategoria?.subcategorias?.filter(
    (sub) => sub.activo
  ) || [];

  if (loading) {
    return <CircularProgress size={30} />;
  }

  return (
    <Stack spacing={2}>
      {error && <Alert severity="error">{error}</Alert>}

      <FormControl fullWidth>
        <InputLabel id="categoria-select-label">
          {required ? 'Categoría *' : 'Categoría'}
        </InputLabel>
        <Select
          labelId="categoria-select-label"
          id="categoria-select"
          value={selectedCategoriaId || ''}
          onChange={(e) => handleCategoriaChange(e.target.value as number | '')}
          label={required ? 'Categoría *' : 'Categoría'}
        >
          <MenuItem value="">
            <em>Seleccionar</em>
          </MenuItem>
          {activeCategorias.map((categoria) => (
            <MenuItem key={categoria.id} value={categoria.id}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography sx={{ fontSize: '1.5rem' }}>
                  {categoria.icono}
                </Typography>
                <Typography>{categoria.nombre}</Typography>
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {selectedCategoriaId && (
        <FormControl fullWidth>
          <InputLabel id="subcategoria-select-label">
            {required ? 'Subcategoría *' : 'Subcategoría'}
          </InputLabel>
          <Select
            labelId="subcategoria-select-label"
            id="subcategoria-select"
            value={selectedSubcategoriaId || ''}
            onChange={(e) => handleSubcategoriaChange(e.target.value as number | '')}
            label={required ? 'Subcategoría *' : 'Subcategoría'}
          >
            <MenuItem value="">
              <em>Seleccionar</em>
            </MenuItem>
            {availableSubcategorias.map((subcategoria) => (
              <MenuItem key={subcategoria.id} value={subcategoria.id}>
                {subcategoria.nombre}
                {(subcategoria.productosCount ?? 0) > 0 && (
                  <Typography
                    variant="caption"
                    sx={{ ml: 1, color: 'textSecondary' }}
                  >
                    ({subcategoria.productosCount})
                  </Typography>
                )}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {selectedCategoriaId && availableSubcategorias.length === 0 && (
        <Typography variant="caption" color="textSecondary">
          Esta categoría no tiene subcategorías disponibles.
        </Typography>
      )}
    </Stack>
  );
}
